/**
 * ChartControls Component
 * 
 * Control panel for chart configuration with date range, scale, granularity,
 * and line visibility toggles. Uses design system tokens for styling.
 */

import React from 'react';
import { typography, surfaces, borders, textColors, brandColors } from '../../design-system/tokens';
import { calculateDateRange } from '../utils/chartHelpers';

const ChartControls = ({
  // Date range
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPresetChange,
  
  // Scale (for time-series chart)
  scale,
  onScaleChange,
  showScaleToggle = false,
  
  // Granularity (for bar charts)
  granularity,
  onGranularityChange,
  showGranularitySelect = false,
  
  // Line visibility (for time-series chart)
  showUsers,
  showContainers,
  onToggleUsers,
  onToggleContainers,
  showLineToggles = false,
}) => {
  // Handle preset selection
  const handlePresetClick = (preset) => {
    const { startDate: newStart, endDate: newEnd } = calculateDateRange(preset);
    onStartDateChange(newStart);
    onEndDateChange(newEnd);
    if (onPresetChange) {
      onPresetChange(preset);
    }
  };

  return (
    <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-4 mb-6`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
        {/* Date Range Section */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0">
          <label className={`${typography.label} ${textColors.secondary}`}>
            Date Range:
          </label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0 w-full sm:w-auto">
              <span className={`${typography.bodySmall} ${textColors.tertiary} w-10 shrink-0 sm:hidden`}>
                From
              </span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                className={`
                  ${surfaces.glassCard} 
                  ${borders.glass} 
                  ${textColors.secondary}
                  border rounded px-3 py-1.5
                  text-sm
                  focus:outline-none focus:ring-2 ${brandColors.focus}
                  w-full sm:w-auto min-w-0
                `}
              />
            </div>

            <span className={`${textColors.tertiary} hidden sm:inline`}>to</span>

            <div className="flex items-center gap-2 min-w-0 w-full sm:w-auto">
              <span className={`${typography.bodySmall} ${textColors.tertiary} w-10 shrink-0 sm:hidden`}>
                To
              </span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                className={`
                  ${surfaces.glassCard} 
                  ${borders.glass} 
                  ${textColors.secondary}
                  border rounded px-3 py-1.5
                  text-sm
                  focus:outline-none focus:ring-2 ${brandColors.focus}
                  w-full sm:w-auto min-w-0
                `}
              />
            </div>
          </div>
        </div>

        {/* Quick Presets */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0">
          <span className={`${typography.label} ${textColors.secondary}`}>Quick:</span>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2 min-w-0">
            {[
              { label: '7 days', value: 'last7days' },
              { label: '30 days', value: 'last30days' },
              { label: '90 days', value: 'last90days' },
              { label: 'All time', value: 'alltime' },
            ].map((preset) => (
              <button
                key={preset.value}
                onClick={() => handlePresetClick(preset.value)}
                className={`
                  ${surfaces.glassCard}
                  ${borders.glass}
                  ${textColors.secondary}
                  ${surfaces.hover}
                  ${typography.bodySmall}
                  border rounded px-3 py-1.5
                  transition-colors duration-150
                  w-full sm:w-auto
                `}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scale Toggle (for time-series) */}
        {showScaleToggle && (
          <div className="flex items-center gap-2 sm:ml-auto">
            <label className={`${typography.label} ${textColors.secondary}`}>
              Scale:
            </label>
            <div className="flex gap-1">
              <button
                onClick={() => onScaleChange('linear')}
                className={`
                  px-3 py-1.5 rounded
                  ${typography.bodySmall}
                  ${scale === 'linear' 
                    ? `${brandColors.primaryBg} text-white` 
                    : `${surfaces.glassCard} ${borders.glass} ${textColors.secondary} border`
                  }
                  transition-colors duration-150
                `}
              >
                Linear
              </button>
              <button
                onClick={() => onScaleChange('log')}
                className={`
                  px-3 py-1.5 rounded
                  ${typography.bodySmall}
                  ${scale === 'log' 
                    ? `${brandColors.primaryBg} text-white` 
                    : `${surfaces.glassCard} ${borders.glass} ${textColors.secondary} border`
                  }
                  transition-colors duration-150
                `}
              >
                Log
              </button>
            </div>
          </div>
        )}

        {/* Granularity Select (for bar charts) */}
        {showGranularitySelect && (
          <div className="flex items-center gap-2 sm:ml-auto">
            <label className={`${typography.label} ${textColors.secondary}`}>
              Granularity:
            </label>
            <select
              value={granularity}
              onChange={(e) => onGranularityChange(e.target.value)}
              className={`
                ${surfaces.glassCard} 
                ${borders.glass} 
                ${textColors.secondary}
                ${typography.bodySmall}
                border rounded px-3 py-1.5
                focus:outline-none focus:ring-2 ${brandColors.focus}
              `}
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
        )}
      </div>

      {/* Line Visibility Toggles (for time-series) */}
      {showLineToggles && (
        <div className={`flex flex-wrap items-center gap-4 mt-3 pt-3 border-t ${borders.glass}`}>
          <span className={`${typography.label} ${textColors.secondary}`}>
            Show:
          </span>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showUsers}
              onChange={onToggleUsers}
              className="w-4 h-4 rounded accent-blue-600 dark:accent-blue-500 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-500"
            />
            <span className={`${typography.bodySmall} ${textColors.secondary}`}>
              Users
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showContainers}
              onChange={onToggleContainers}
              className="w-4 h-4 rounded accent-blue-600 dark:accent-blue-500 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-500"
            />
            <span className={`${typography.bodySmall} ${textColors.secondary}`}>
              Containers
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default ChartControls;

